module.exports = {
  title: 'Central de Ajuda Macmillan',
  intro: 'Guia prático do SalesFlow para consultar produtos, montar pedidos, acompanhar documentos, administrar doações e tratar devoluções. Os nomes de menus e botões foram mantidos para facilitar a localização de cada tarefa.',
  updatedAt: '2026-09-10',
  roles: [
    { id: 'cliente', label: 'Cliente', summary: 'Consulta catálogo, cria e acompanha pedidos próprios conforme as opções liberadas para sua conta.' },
    { id: 'representante', label: 'Representante', summary: 'Atende clientes vinculados, prepara pedidos e doações e acompanha suas operações.' },
    { id: 'supervisor', label: 'Supervisor', summary: 'Acompanha a equipe, distribui budget e analisa doações submetidas à aprovação.' },
    { id: 'comercial', label: 'Comercial', summary: 'Opera pedidos e consultas comerciais dentro da carteira e das permissões recebidas.' },
    { id: 'administrador', label: 'Administrador', summary: 'Administra acessos, preferências e regras operacionais disponibilizadas para a empresa.' },
  ],
  sections: [
    {
      id: 'primeiros-passos',
      title: 'Primeiros passos',
      summary: 'Acesso seguro, perfil ativo e encerramento da sessão.',
      icon: 'LogInIcon',
      articles: [
        {
          id: 'entrar-no-portal',
          title: 'Entrar no portal',
          summary: 'Como iniciar uma sessão e reconhecer problemas de acesso.',
          roles: [],
          keywords: ['login', 'entrar', 'acesso', 'senha'],
          paragraphs: [
            'Use o endereço do portal fornecido pela Macmillan e informe seu usuário e sua senha. Confira se está no ambiente correto antes de digitar os dados.',
            'Depois da autenticação, o conteúdo exibido considera sua empresa, seus vínculos e o perfil ativo. Menus diferentes entre colegas nem sempre representam erro.',
          ],
          steps: [
            'Abra o endereço oficial do SalesFlow.',
            'Preencha os campos de usuário e senha.',
            'Selecione Entrar e aguarde a página inicial.',
            'Confirme seu nome e o perfil ativo antes de iniciar uma operação.',
          ],
          tips: ['Salve o endereço oficial nos favoritos para evitar páginas incorretas.', 'Se houver mais de um perfil, escolha aquele relacionado à tarefa que será executada.'],
          warnings: ['Não compartilhe senha nem código de acesso.', 'Após tentativas sem sucesso, confira o usuário antes de repetir para evitar bloqueio.'],
        },
        {
          id: 'alterar-meus-dados-e-senha',
          title: 'Consultar Meu Perfil e alterar a senha',
          summary: 'Revise o cadastro pessoal e mantenha uma senha exclusiva.',
          roles: [],
          keywords: ['meus dados', 'perfil', 'senha', 'cadastro'],
          paragraphs: [
            'Meu Perfil, no menu do usuário, reúne as informações pessoais disponíveis para consulta. Alguns campos podem ser apenas informativos.',
            'Alterar senha é outra opção nesse mesmo menu. Informe a senha atual, uma nova senha com pelo menos 8 caracteres e a confirmação. Ao concluir, a sessão é encerrada: entre novamente com a nova senha.',
          ],
          steps: [
            'Abra o menu do usuário e selecione Meu Perfil.',
            'Revise os campos disponíveis e corrija somente dados sob sua responsabilidade.',
            'Feche Meu Perfil e selecione Alterar senha no menu do usuário. Preencha a senha atual, a nova senha e a confirmação.',
            'Confirme em Alterar senha. Após o encerramento da sessão, entre novamente com a nova senha.',
          ],
          tips: ['Atualize e-mail e telefone quando esses campos estiverem liberados.', 'Digite a nova senha duas vezes com atenção a letras maiúsculas.'],
          warnings: ['Se um dado corporativo estiver bloqueado, solicite a correção ao responsável pelo cadastro.', 'Nunca envie sua senha por e-mail ou mensagem.'],
        },
        {
          id: 'trocar-perfil-e-sair',
          title: 'Trocar o perfil ativo e sair',
          summary: 'Evite operar com o papel errado e encerre o acesso com segurança.',
          roles: [],
          keywords: ['perfil ativo', 'trocar perfil', 'logout', 'sair'],
          paragraphs: [
            'Quando uma pessoa possui mais de um perfil, o perfil ativo determina clientes, tipos de pedido, menus e ações permitidos naquele momento.',
            'Sair encerra a sessão. Fechar apenas a aba pode deixar o acesso aberto no navegador, especialmente em computador compartilhado.',
          ],
          steps: [
            'Abra o menu do usuário no canto superior da tela.',
            'Use a opção de troca de perfil, quando disponível, e selecione o papel desejado.',
            'Confira novamente o nome do perfil exibido.',
            'Ao terminar, abra o menu do usuário e selecione Sair.',
          ],
          tips: ['Troque o perfil antes de começar um carrinho; não espere a etapa final.', 'Em equipamento compartilhado, feche também o navegador depois de sair.'],
          warnings: ['Uma opção permitida em um perfil pode não existir em outro.', 'Não presuma que somar seus perfis amplia a permissão da operação atual: vale o perfil ativo.'],
        },
      ],
    },
    {
      id: 'perfis-e-permissoes',
      title: 'Perfis e permissões',
      summary: 'Entenda quem pode fazer cada operação e por que menus variam.',
      icon: 'UsersIcon',
      articles: [
        {
          id: 'entender-os-perfis',
          title: 'Entender os perfis',
          summary: 'Visão simples dos papéis de cliente, representante, supervisor, comercial e administrador.',
          roles: [],
          keywords: ['perfil', 'papel', 'permissão', 'acesso'],
          paragraphs: [
            'Cliente atua em sua própria conta; Representante atende vínculos; Supervisor acompanha equipe e aprova operações; Comercial trabalha a rotina comercial; Administrador cuida das opções administrativas liberadas.',
            'O perfil não concede sozinho todos os tipos de pedido. A Macmillan pode relacionar tipos específicos a cada perfil, e essa configuração é a regra efetiva.',
          ],
          steps: [
            'Identifique o perfil mostrado no cabeçalho ou menu do usuário.',
            'Abra o menu necessário e confira as ações oferecidas.',
            'Se a tarefa pertencer a outro perfil seu, faça a troca antes de continuar.',
            'Se a opção continuar ausente, peça ao responsável que confira seu vínculo e a configuração do tipo.',
          ],
          tips: ['Ao pedir ajuda, informe o perfil ativo e o nome exato do menu.', 'Compare permissões somente entre pessoas com a mesma função e carteira.'],
          warnings: ['Não use a conta de outra pessoa para contornar uma permissão.', 'Planilhas antigas de perfis não substituem a configuração atual do portal.'],
        },
        {
          id: 'quem-pode-criar-cada-tipo',
          title: 'Quem pode criar cada tipo de pedido',
          summary: 'A autorização depende do perfil ativo e dos tipos permitidos.',
          roles: [],
          keywords: ['criação', 'tipo de pedido', 'WS', 'WC', 'WD'],
          paragraphs: [
            'Toda criação ou edição valida se o tipo escolhido está ligado ao perfil ativo. Portanto, a lista apresentada na tela é mais confiável que uma regra informal sobre cargos.',
            'Cliente não cria WD. Em outros perfis, inclusive Representante, Supervisor ou Comercial, a disponibilidade de WS, WC ou WD depende dos tipos explicitamente permitidos pela configuração da Macmillan.',
          ],
          steps: [
            'Ative o perfil com o qual a operação deve ser registrada.',
            'Inicie o pedido e consulte os tipos oferecidos pela tela.',
            'Escolha apenas o tipo correspondente à finalidade real.',
            'Se um tipo esperado não aparecer, não escolha outro como atalho; solicite conferência da permissão.',
          ],
          tips: ['Informe cliente, perfil ativo e tipo desejado ao abrir uma solicitação de acesso.', 'Uma preferência pode deixar o Representante somente com WD; quando ativa, ela considera o perfil atual.'],
          warnings: ['Não existe promessa geral de que Representante ou Supervisor trabalhe apenas com WD.', 'Alterar o perfil durante a montagem pode exigir reiniciar a operação.'],
        },
        {
          id: 'clientes-carteiras-e-escopo',
          title: 'Clientes, carteiras e escopo de consulta',
          summary: 'Por que cada usuário enxerga conjuntos diferentes de clientes e documentos.',
          roles: ['representante', 'supervisor', 'comercial', 'administrador'],
          keywords: ['cliente', 'carteira', 'vínculo', 'escopo'],
          paragraphs: [
            'As buscas respeitam os clientes vinculados ao usuário e ao perfil ativo. Supervisores podem ter visão da equipe, enquanto representantes normalmente trabalham sua carteira.',
            'O mesmo escopo protege pedidos, boletos, notas e endereços. Uma busca vazia pode indicar ausência de vínculo, e não ausência definitiva do registro.',
          ],
          steps: [
            'Confirme o perfil ativo.',
            'Pesquise o cliente por código, documento ou nome nos campos disponíveis.',
            'Remova filtros muito restritivos e tente novamente.',
            'Se o cliente não aparecer, solicite conferência do vínculo ao responsável.',
          ],
          tips: ['Use o código do cliente quando houver nomes parecidos.', 'Registre qual filtro foi usado antes de pedir suporte.'],
          warnings: ['Não selecione cliente de nome semelhante sem conferir código e documento.', 'Acesso a um cliente não significa acesso automático a toda a equipe.'],
        },
      ],
    },
    {
      id: 'inicio-e-mural',
      title: 'Início, mural e painéis',
      summary: 'Leia comunicados e use indicadores como ponto de partida.',
      icon: 'HomeIcon',
      articles: [
        {
          id: 'usar-a-pagina-inicial',
          title: 'Usar a página inicial',
          summary: 'Localize atalhos, catálogo e informações da sessão.',
          roles: [],
          keywords: ['início', 'home', 'atalhos', 'catálogo'],
          paragraphs: [
            'A página inicial pode reunir seleção de cliente, tabela de preço, categorias, catálogo e atalhos. O conteúdo muda conforme o perfil e as preferências ativas.',
            'Antes de adicionar itens, confira o contexto do pedido. Cliente ou tabela incorretos podem obrigar a refazer o carrinho.',
          ],
          steps: ['Acesse Início.', 'Confira perfil, cliente e tabela selecionados.', 'Leia alertas ou comunicados visíveis.', 'Use o catálogo ou o atalho adequado para começar.'],
          tips: ['Se a tela parecer incompleta, confirme se o cliente foi selecionado.', 'Retorne ao Início para iniciar uma compra pelo catálogo.'],
          warnings: ['Não reutilize um contexto deixado por outra pessoa em computador compartilhado.', 'Valores e opções só são definitivos depois das validações do pedido.'],
        },
        {
          id: 'acompanhar-o-mural',
          title: 'Acompanhar o mural',
          summary: 'Consulte comunicados operacionais sem perder avisos importantes.',
          roles: [],
          keywords: ['mural', 'aviso', 'comunicado', 'notícia'],
          paragraphs: [
            'O mural é usado para orientações, campanhas e avisos. Leia título, período e público do comunicado antes de aplicar uma informação à sua rotina.',
            'Um aviso pode complementar o manual durante uma campanha. Quando houver conflito, confirme a regra vigente com o responsável indicado.',
          ],
          steps: ['Abra Mural ou consulte os cartões da página inicial.', 'Selecione o comunicado para ler o conteúdo completo.', 'Observe datas e anexos realmente disponíveis.', 'Anote a ação necessária e o prazo.'],
          tips: ['Revise o mural no início do dia.', 'Use filtros de data ou assunto, se apresentados.'],
          warnings: ['Não suponha que um comunicado antigo continua vigente.', 'A disponibilidade de anexo deve ser confirmada na própria publicação.'],
        },
        {
          id: 'interpretar-paineis',
          title: 'Interpretar painéis e indicadores',
          summary: 'Use totais e gráficos para localizar operações que precisam de atenção.',
          roles: ['representante', 'supervisor', 'comercial', 'administrador'],
          keywords: ['dashboard', 'painel', 'indicador', 'filtro'],
          paragraphs: [
            'Painéis resumem registros dentro do período, perfil e carteira selecionados. Eles ajudam a priorizar trabalho, mas não substituem o detalhe de cada pedido.',
            'Ao comparar números, use os mesmos filtros. Atualizações de integração podem fazer o detalhe aparecer em momento diferente do resumo.',
          ],
          steps: ['Abra o painel relacionado à sua operação.', 'Defina período e filtros.', 'Leia a legenda e o significado de cada situação.', 'Clique no indicador, quando possível, para conferir a lista de origem.'],
          tips: ['Atualize a página antes de fechar uma conferência.', 'Compare totais somente com o mesmo período.'],
          warnings: ['Não interprete ausência momentânea como cancelamento.', 'Não use captura antiga como posição atual.'],
        },
      ],
    },
    {
      id: 'catalogo-e-produtos',
      title: 'Catálogo e produtos',
      summary: 'Pesquise ISBN, confira estoque, preço e impostos.',
      icon: 'BookOpenIcon',
      articles: [
        {
          id: 'pesquisar-no-catalogo',
          title: 'Pesquisar no catálogo',
          summary: 'Encontre o produto correto por ISBN, título ou referência.',
          roles: [],
          keywords: ['catálogo', 'ISBN', 'EAN', 'livro', 'busca'],
          paragraphs: [
            'A pesquisa pode aceitar ISBN ou EAN, título e outras referências exibidas. O ISBN é a opção mais segura quando há edições com nomes semelhantes.',
            'A disponibilidade do produto considera o contexto selecionado. Um item pode não ser oferecido para determinada categoria ou tabela.',
          ],
          steps: ['Selecione cliente e tabela de preço.', 'Digite o ISBN completo ou parte do título.', 'Aplique os filtros necessários.', 'Abra o produto e confirme edição, idioma e código antes de adicionar.'],
          tips: ['Pesquise o ISBN sem espaços quando a busca não retornar resultado.', 'Limpe filtros antigos antes de concluir que o item não existe.'],
          warnings: ['Não escolha pela capa apenas.', 'Exemplares de títulos parecidos podem ter códigos e preços diferentes.'],
        },
        {
          id: 'conferir-estoque-preco-e-impostos',
          title: 'Conferir estoque, preço e impostos',
          summary: 'Entenda os valores apresentados antes da finalização.',
          roles: [],
          keywords: ['estoque', 'preço', 'imposto', 'tabela'],
          paragraphs: [
            'Preço depende da tabela e do cliente. Estoque e imposto são verificados no contexto atual e podem ser recalculados durante a montagem e a finalização.',
            'O total pode mudar quando quantidade, condição, endereço ou itens mudam. Sempre revise o resumo final.',
          ],
          steps: ['Confirme cliente e tabela.', 'Abra o item e observe preço e disponibilidade exibidos.', 'Informe a quantidade.', 'Revise subtotal, impostos e total no carrinho e no fechamento.'],
          tips: ['Atualize a consulta se o catálogo ficou aberto por muito tempo.', 'Para divergências, anote ISBN, tabela, quantidade e horário.'],
          warnings: ['A exibição no catálogo não reserva estoque.', 'Não prometa valor antes da confirmação final do pedido.'],
        },
        {
          id: 'entender-autocomplete-e-valores-atuais',
          title: 'Entender o autocomplete e os valores atuais',
          summary: 'Resultados sugeridos são referências; preço, estoque e imposto são atuais.',
          roles: ['representante', 'supervisor', 'comercial', 'administrador'],
          keywords: ['autocomplete', 'SKU', 'referência', 'preço atual'],
          paragraphs: [
            'No Novo Pedido, o autocomplete ajuda a localizar grade, item ou família comercial. Se uma referência não resolver pela grade, a pesquisa ainda pode encontrar o item ou a família correspondente.',
            'O sistema usa os valores atuais de estoque, preço e imposto para montar a linha. O autocomplete não recupera preço histórico de pedido antigo.',
          ],
          steps: ['Digite ISBN, SKU ou descrição no campo do item.', 'Aguarde a lista de sugestões.', 'Confira código e descrição e selecione o resultado correto.', 'Revise os valores atuais calculados antes de salvar.'],
          tips: ['Digite mais caracteres para reduzir resultados parecidos.', 'Se precisar reproduzir uma negociação antiga, confirme a tabela vigente em vez de copiar o preço.'],
          warnings: ['Não trate a sugestão como item selecionado até clicar nela.', 'Preço histórico não é garantia de preço atual.'],
        },
      ],
    },
    {
      id: 'categorias-de-pedido',
      title: 'WS, WC e WD',
      summary: 'Escolha a finalidade certa e não misture categorias.',
      icon: 'TagIcon',
      articles: [
        {
          id: 'diferenca-entre-ws-wc-wd',
          title: 'Diferença entre WS, WC e WD',
          summary: 'Venda, certificação e doação têm finalidades e regras distintas.',
          roles: [],
          keywords: ['WS', 'WC', 'WD', 'venda', 'certificação', 'doação'],
          paragraphs: [
            'WS significa Venda, WC significa Certificação e WD significa Doação. A escolha afeta produtos aceitos, validações, aprovação e campos exibidos.',
            'WD não tem valor mínimo. Isso não elimina as regras de permissão, budget, categoria ELT ou GL e aprovação.',
          ],
          steps: ['Defina a finalidade real da operação.', 'Confirme se o tipo aparece para o perfil ativo.', 'Selecione WS, WC ou WD antes de montar os itens.', 'Revise o tipo no resumo final.'],
          tips: ['Se houver dúvida entre WS e WC, confirme o objetivo comercial antes de iniciar.', 'Use WD somente para doação autorizada.'],
          warnings: ['Não escolha WD para evitar regras de venda.', 'Cliente não cria WD.'],
        },
        {
          id: 'nao-misturar-ws-e-wc',
          title: 'Não misturar WS e WC',
          summary: 'Cada carrinho ou pedido de cliente deve permanecer em uma categoria.',
          roles: [],
          keywords: ['mistura', 'categoria incompatível', 'WS', 'WC'],
          paragraphs: [
            'Os produtos são classificados para WS ou WC. Um pedido não aceita itens das duas categorias ao mesmo tempo, mesmo que pertençam ao mesmo cliente.',
            'Quando uma importação contém mais de uma categoria, a tela pede Qual categoria deseja usar? Os itens das demais categorias não são adicionados.',
          ],
          steps: ['Escolha a categoria do pedido.', 'Adicione ou importe os itens.', 'Revise os indicadores de categoria.', 'Crie outro pedido separado para os itens incompatíveis.'],
          tips: ['Separe sua lista por categoria antes de começar.', 'Na importação, confira quais linhas ficarão de fora antes de confirmar.'],
          warnings: ['Não altere códigos para forçar compatibilidade.', 'A validação final pode rejeitar uma mistura que passou despercebida na montagem.'],
        },
        {
          id: 'pic-e-regras-condicionais',
          title: 'PIC e regras condicionais',
          summary: 'Algumas opções só aparecem quando a preferência correspondente está ativa.',
          roles: [],
          keywords: ['PIC', 'preferência', 'configuração', 'catálogo'],
          paragraphs: [
            'Recursos como PIC podem ser exibidos apenas para determinados clientes, perfis, campanhas ou configurações da Macmillan. Se não aparecerem, a operação padrão continua sendo pelo catálogo ou por Novo Pedido.',
            'A existência do nome PIC em uma orientação não garante que ele esteja ativo para sua sessão. A tela atual é a referência operacional.',
          ],
          steps: ['Confirme perfil e cliente.', 'Procure a opção PIC no menu ou contexto indicado.', 'Se disponível, siga os campos e validações mostrados.', 'Se ausente e necessário, peça confirmação da configuração ao responsável.'],
          tips: ['Informe o cliente e o perfil ao perguntar sobre PIC.', 'Não confunda campanha comercial com tipo WS, WC ou WD.'],
          warnings: ['Não interrompa um pedido comum esperando um recurso que não esteja habilitado.', 'Não use instruções de outro tenant como regra da Macmillan.'],
        },
      ],
    },
    {
      id: 'carrinho',
      title: 'Carrinho',
      summary: 'Adicione, edite, remova, cancele ou continue uma compra.',
      icon: 'ShoppingCartIcon',
      articles: [
        {
          id: 'adicionar-e-editar-itens',
          title: 'Adicionar e editar itens',
          summary: 'Monte o carrinho e revise quantidades.',
          roles: [],
          keywords: ['carrinho', 'adicionar', 'quantidade', 'editar'],
          paragraphs: [
            'Adicionar ao carrinho reúne os produtos antes da finalização. Alterar quantidade pode recalcular disponibilidade, impostos e total.',
            'Confira cada ISBN e a quantidade desejada. O resultado da atualização deve aparecer no resumo do carrinho.',
          ],
          steps: ['Localize o produto.', 'Informe a quantidade e selecione Adicionar ao carrinho.', 'Abra o carrinho.', 'Edite a quantidade e confirme a atualização.'],
          tips: ['Faça alterações uma por vez quando estiver investigando uma divergência.', 'Revise o total após cada edição importante.'],
          warnings: ['Não deixe quantidade zero esperando que o item seja mantido.', 'A alteração não reserva estoque até o pedido ser aceito.'],
        },
        {
          id: 'remover-ou-cancelar-carrinho',
          title: 'Remover item ou cancelar o carrinho',
          summary: 'Saiba a diferença entre excluir uma linha e abandonar toda a montagem.',
          roles: [],
          keywords: ['remover', 'cancelar', 'limpar carrinho', 'excluir'],
          paragraphs: [
            'Remover atua sobre o item escolhido. Cancelar ou limpar o carrinho pode descartar toda a montagem, conforme a confirmação exibida.',
            'Antes de cancelar, verifique se a intenção era apenas voltar ao catálogo. Use Continuar comprando para preservar os itens.',
          ],
          steps: ['Abra o carrinho.', 'Para uma linha, use Remover e confirme.', 'Para abandonar tudo, use Cancelar ou Limpar quando disponível.', 'Confira se o carrinho ficou no estado esperado.'],
          tips: ['Leia a mensagem de confirmação antes de aceitar.', 'Anote os ISBNs se precisar reconstruir uma lista longa.'],
          warnings: ['Uma exclusão confirmada pode não ser recuperável.', 'Não confunda cancelar carrinho com cancelar pedido já enviado.'],
        },
        {
          id: 'continuar-comprando-e-retomar',
          title: 'Continuar comprando e retomar',
          summary: 'Volte ao catálogo sem perder a montagem atual.',
          roles: [],
          keywords: ['continuar comprando', 'retomar', 'carrinho'],
          paragraphs: [
            'Continuar comprando retorna ao catálogo mantendo o contexto do carrinho. Ao retomar, confirme se cliente, tabela e tipo continuam corretos.',
            'Sessão expirada, troca de perfil ou mudança de contexto pode impedir a continuidade. Não conte com o navegador como forma de guardar um pedido.',
          ],
          steps: ['No carrinho, selecione Continuar comprando.', 'Adicione os demais produtos.', 'Abra novamente o carrinho.', 'Revise contexto, itens, quantidades e totais.'],
          tips: ['Para interrupções longas, use rascunho quando essa opção estiver habilitada.', 'Finalize a revisão na mesma sessão sempre que possível.'],
          warnings: ['Não troque cliente no meio da montagem sem observar os avisos.', 'Fechar a aba não equivale a salvar rascunho.'],
        },
      ],
    },
    {
      id: 'criacao-de-pedidos',
      title: 'Criação de pedidos',
      summary: 'Use o catálogo ou o fluxo Operações com segurança.',
      icon: 'PlusCircleIcon',
      articles: [
        {
          id: 'criar-pelo-catalogo',
          title: 'Criar pedido pelo catálogo',
          summary: 'Fluxo cotidiano da página inicial até o carrinho.',
          roles: [],
          keywords: ['catálogo', 'criar pedido', 'home', 'finalizar'],
          paragraphs: [
            'O fluxo pelo catálogo é indicado para pesquisar visualmente e adicionar produtos aos poucos. Selecione o contexto antes de importar ou incluir itens.',
            'Ao final, o carrinho conduz à escolha de condições e endereço. O pedido só segue depois das validações apresentadas.',
          ],
          steps: ['Na página inicial, escolha tipo, cliente e tabela de preço.', 'Pesquise e adicione produtos ao carrinho.', 'Revise itens e quantidades.', 'Avance para finalizar, complete os dados e confirme.'],
          tips: ['Confira o cliente antes do primeiro item.', 'Use o ISBN para reduzir erros de edição.'],
          warnings: ['WS e WC não podem ser misturados.', 'Não feche a janela durante o processamento final.'],
        },
        {
          id: 'criar-em-operacoes-novo-pedido',
          title: 'Criar em Operações com Novo Pedido',
          summary: 'Fluxo operacional para incluir grupos e linhas diretamente.',
          roles: [],
          keywords: ['Operações', 'Pedidos', 'Novo Pedido', 'autocomplete'],
          paragraphs: [
            'Quando o módulo estiver liberado para seu perfil, abra Pedidos e selecione Novo Pedido. Esse fluxo permite estruturar o pedido diretamente e localizar itens por autocomplete.',
            'A criação continua sujeita às mesmas regras de perfil, categoria, valores atuais, endereço e aprovação.',
          ],
          steps: ['Acesse Operações, Pedidos.', 'Selecione Novo Pedido.', 'Preencha tipo, cliente, tabela e demais dados iniciais.', 'Inclua os itens, revise o resumo e use a ação de salvar ou enviar apresentada.'],
          tips: ['Aguarde a seleção do autocomplete preencher a linha.', 'Revise cada grupo antes de concluir.'],
          warnings: ['Não copie preço de pedido anterior para substituir o cálculo atual.', 'Se o tipo não for permitido ao perfil ativo, a operação será impedida.'],
        },
        {
          id: 'importar-pedido-por-csv',
          title: 'Importar pedido por CSV',
          summary: 'Formato real do modal e revisão antes de adicionar.',
          roles: [],
          keywords: ['CSV', 'Importar CSV', 'código', 'quantidade'],
          paragraphs: [
            'O modal Importar pedido por CSV informa o formato código;quantidade, com um produto por linha e sem cabeçalho. O código é o código interno do produto, ISBN ou EAN.',
            'Exemplo fictício: 9780000000002;3. A análise mostra Encontrados, Sem preço, Não encontrados e quantidade total. Somente itens com preço e da categoria escolhida são adicionados.',
          ],
          steps: ['Escolha primeiro o cliente e a tabela de preço.', 'Selecione Importar CSV ou Importar via CSV.', 'Escolha o arquivo .csv e selecione Analisar.', 'Revise todas as linhas e use Adicionar ao carrinho ou Adicionar ao pedido.'],
          tips: ['Use ponto e vírgula entre código e quantidade.', 'Corrija linhas ignoradas no arquivo e analise novamente.'],
          warnings: ['Não use o cabeçalho de uma planilha de estoque; este modal espera linhas código;quantidade.', 'Ao escolher uma categoria para arquivo misto, itens das outras categorias não serão adicionados.'],
        },
      ],
    },
    {
      id: 'finalizacao-e-entrega',
      title: 'Finalização e entrega',
      summary: 'Condições, endereços, Nexus e conferência final.',
      icon: 'CheckCircleIcon',
      articles: [
        {
          id: 'revisar-checkout',
          title: 'Revisar o checkout',
          summary: 'Faça a última conferência antes de enviar.',
          roles: [],
          keywords: ['checkout', 'finalizar', 'resumo', 'condição'],
          paragraphs: [
            'A finalização reúne cliente, tipo, tabela, condição de pagamento, endereço, itens, impostos e totais. Trate essa tela como a conferência final.',
            'O resultado pode ser pedido enviado, rascunho ou WD aguardando aprovação. Leia a confirmação para saber qual foi o desfecho.',
          ],
          steps: ['Confira cliente e finalidade.', 'Revise condição de pagamento e endereço.', 'Confira ISBNs, quantidades, preços, impostos e total.', 'Confirme somente depois de corrigir todos os avisos.'],
          tips: ['Faça a conferência com o documento de compra do cliente ao lado.', 'Guarde os números exibidos na confirmação.'],
          warnings: ['Não clique repetidamente enquanto a tela estiver processando.', 'A confirmação de gravação não significa necessariamente aceite imediato pelo ERP.'],
        },
        {
          id: 'escolher-endereco-de-entrega',
          title: 'Escolher endereço de entrega',
          summary: 'Use apenas endereços válidos para o cliente e a operação.',
          roles: [],
          keywords: ['endereço', 'entrega', 'UF', 'cliente'],
          paragraphs: [
            'Os endereços disponíveis dependem do cadastro, do cliente e das permissões atuais. Regras antigas de planilha sobre quem pode escolher endereço não são autoridade para a tela atual.',
            'A UF é validada. Se for usado endereço alternativo, os dados são guardados como retrato do pedido; alterar o cadastro depois não muda pedidos passados.',
          ],
          steps: ['Abra a seleção de endereço no fechamento.', 'Confira destinatário, logradouro, número, cidade, UF e CEP.', 'Escolha o endereço correto ou preencha o alternativo quando permitido.', 'Revise o endereço no resumo antes de confirmar.'],
          tips: ['Confirme dados de entrega diretamente com o cliente.', 'Peça correção cadastral quando o endereço oficial estiver incorreto.'],
          warnings: ['Não escolha endereço aproximado.', 'Uma UF inválida impede o envio.'],
        },
        {
          id: 'preencher-id-nexus',
          title: 'Preencher ID Nexus',
          summary: 'Campo opcional somente em WD e com exatamente seis dígitos.',
          roles: ['representante', 'supervisor', 'comercial', 'administrador'],
          keywords: ['ID Nexus', 'Nexus', 'seis dígitos', 'WD'],
          paragraphs: [
            'O ID Nexus é opcional exclusivamente em pedidos WD. Quando informado, deve conter exatamente seis dígitos.',
            'Em WS e WC, os campos Nexus ficam ocultos e não são enviados. A regra depende da finalidade do pedido, não de o usuário ser interno ou externo.',
          ],
          steps: ['Confirme que o pedido é WD.', 'Localize o campo ID Nexus, se exibido.', 'Deixe vazio quando não houver código ou informe exatamente seis números.', 'Revise e continue a finalização.'],
          tips: ['Remova espaços, pontos e hífens.', 'Confirme o código com a origem responsável antes de informar.'],
          warnings: ['Não preencha um código fictício.', 'Não procure nem exija ID Nexus em WS ou WC.'],
        },
      ],
    },
    {
      id: 'rascunhos',
      title: 'Rascunhos',
      summary: 'Salve trabalho incompleto sem confundir com aprovação.',
      icon: 'Edit3Icon',
      articles: [
        {
          id: 'salvar-como-rascunho',
          title: 'Salvar como rascunho',
          summary: 'Preserve uma montagem quando a função estiver habilitada.',
          roles: [],
          keywords: ['rascunho', 'salvar', 'continuar depois'],
          paragraphs: [
            'Rascunho guarda um pedido ainda não submetido. A função pode estar ativa ou inativa e pode ser limitada por perfil ou somente a WD.',
            'Quando a lista de perfis de rascunho não está restringida, a opção pode atender todos os perfis permitidos; a tela mostra o comportamento vigente.',
          ],
          steps: ['Preencha os dados disponíveis do pedido.', 'Selecione Salvar como rascunho, se oferecido.', 'Aguarde a confirmação.', 'Anote o identificador para localizar depois.'],
          tips: ['Escreva observações úteis quando houver campo disponível.', 'Salve antes de uma interrupção longa.'],
          warnings: ['Fechar a tela não salva automaticamente.', 'Rascunho não foi enviado ao ERP nem entrou em aprovação.'],
        },
        {
          id: 'localizar-e-editar-rascunho',
          title: 'Localizar e editar rascunho',
          summary: 'Retome o pedido respeitando perfil e escopo.',
          roles: [],
          keywords: ['editar rascunho', 'localizar', 'permissão'],
          paragraphs: [
            'A visualização e edição seguem o perfil ativo, os vínculos e as preferências de rascunho. Outra pessoa pode não enxergar o mesmo registro.',
            'Ao reabrir, preço, estoque e imposto devem ser novamente conferidos, pois a realidade comercial pode ter mudado.',
          ],
          steps: ['Abra a lista de Pedidos ou Rascunhos.', 'Filtre pela situação Rascunho e pelo cliente.', 'Abra o registro permitido.', 'Atualize, revise e salve novamente ou envie.'],
          tips: ['Confira a data da última alteração.', 'Revalide endereço e condição de pagamento.'],
          warnings: ['Não suponha que valores antigos serão preservados.', 'A mudança de tipo continua sujeita à permissão e compatibilidade dos itens.'],
        },
        {
          id: 'diferenciar-rascunho-e-pendente',
          title: 'Diferenciar rascunho de aguardando aprovação',
          summary: 'Situações parecidas exigem ações diferentes.',
          roles: [],
          keywords: ['rascunho', 'pendente', 'aprovação', 'status'],
          paragraphs: [
            'Rascunho ainda está em preparação. WD aguardando aprovação já foi submetido ao supervisor e segue um processo controlado.',
            'Pedido aceito pelo ERP é outra etapa. Observe a situação exibida e o histórico, sem concluir pelo número do pedido apenas.',
          ],
          steps: ['Abra o detalhe.', 'Leia a situação atual.', 'Consulte histórico ou movimentações.', 'Execute somente a ação compatível: editar rascunho, aguardar, aprovar ou acompanhar ERP.'],
          tips: ['Use filtros de situação na lista.', 'Ao comunicar um caso, informe a situação por extenso.'],
          warnings: ['Não recrie o pedido apenas porque está aguardando aprovação.', 'Não trate rascunho como compromisso confirmado.'],
        },
      ],
    },
    {
      id: 'doacoes-wd',
      title: 'Doações WD',
      summary: 'Prepare doações, separe ELT e GL e envie para aprovação.',
      icon: 'GiftIcon',
      articles: [
        {
          id: 'criar-doacao-wd',
          title: 'Criar uma doação WD',
          summary: 'Pré-requisitos e fluxo básico.',
          roles: ['representante', 'supervisor', 'comercial', 'administrador'],
          keywords: ['WD', 'doação', 'criar'],
          paragraphs: [
            'Para criar WD, o tipo deve estar permitido ao perfil ativo e o cliente deve estar disponível. Cliente não cria WD.',
            'A doação não tem valor mínimo, mas pode consumir budget e precisa passar por aprovação. Produtos, quantidades e justificativa devem ser revisados.',
          ],
          steps: ['Ative um perfil com WD permitido.', 'Escolha cliente e tipo WD.', 'Adicione os itens e complete os campos solicitados.', 'Revise budget e resumo e confirme o envio para aprovação.'],
          tips: ['Prepare a justificativa antes de começar.', 'Confira o responsável supervisor ligado à operação.'],
          warnings: ['Não use WD para venda ou certificação.', 'Permissão de menu não elimina as validações finais.'],
        },
        {
          id: 'separacao-elt-e-gl',
          title: 'Separação entre ELT e GL',
          summary: 'Como o idioma classifica itens e divide uma doação mista.',
          roles: ['representante', 'supervisor', 'comercial', 'administrador'],
          keywords: ['ELT', 'GL', 'budget', 'idioma', 'divisão'],
          paragraphs: [
            'Produtos cujo idioma começa por inglês são classificados em ELT. Itens de outros idiomas ou sem idioma são classificados em GL, denominação atual da categoria antes chamada OL.',
            'Uma WD com ELT e GL é separada em dois pedidos somente depois da confirmação. A tela pode pedir confirmação da divisão e cada parte usa seu próprio pool.',
          ],
          steps: ['Adicione os itens WD.', 'Revise a categoria ELT ou GL indicada para cada produto.', 'Confira os totais separados.', 'Confirme a divisão quando a mensagem for apresentada.'],
          tips: ['Antecipe duas referências de acompanhamento para pedido misto.', 'Verifique saldo nas duas categorias.'],
          warnings: ['Não altere idioma do produto para mudar o pool.', 'Saldo em ELT não cobre automaticamente falta em GL, nem o contrário.'],
        },
        {
          id: 'resultado-do-envio-wd',
          title: 'Resultado do envio de WD',
          summary: 'Entenda a mensagem de aguardando aprovação.',
          roles: ['representante', 'supervisor', 'comercial', 'administrador'],
          keywords: ['WD', 'aguardando aprovação', 'supervisor'],
          paragraphs: [
            'Depois do envio válido, a WD fica aguardando aprovação do supervisor. Isso significa que foi submetida, não que já foi aceita pelo ERP.',
            'Em pedido misto, acompanhe cada pedido criado. Uma parte pode exigir atenção diferente da outra.',
          ],
          steps: ['Leia a confirmação após enviar.', 'Anote todos os números gerados.', 'Abra a lista e filtre por aguardando aprovação.', 'Acompanhe histórico e eventuais devoluções para ajuste.'],
          tips: ['Avise o aprovador pelo canal operacional adotado pela equipe.', 'Evite criar duplicata enquanto a WD estiver pendente.'],
          warnings: ['Rascunho e WD pendente não são a mesma situação.', 'Não comunique a doação como concluída antes do aceite necessário.'],
        },
      ],
    },
    {
      id: 'budget',
      title: 'Budget ELT e GL',
      summary: 'Vigência, pools, alocação, consumo e devolução de saldo.',
      icon: 'PieChartIcon',
      articles: [
        {
          id: 'entender-pools-e-alocacoes',
          title: 'Entender pools e alocações',
          summary: 'Budget do supervisor e parcela destinada ao representante.',
          roles: ['representante', 'supervisor', 'comercial', 'administrador'],
          keywords: ['budget', 'pool', 'alocação', 'saldo'],
          paragraphs: [
            'O budget é organizado por supervisor e categoria ELT ou GL. O supervisor distribui alocações para representantes, e o saldo da alocação corresponde ao valor concedido menos o valor já consumido.',
            'Cada categoria é independente. A consulta deve considerar supervisor, representante, categoria e período de vigência.',
          ],
          steps: ['Abra a área de Budget disponível ao seu perfil.', 'Selecione período e categoria ELT ou GL.', 'Localize o supervisor ou representante.', 'Compare valor alocado, valor gasto e saldo.'],
          tips: ['Confira a categoria antes de investigar falta de saldo.', 'Use o histórico para entender alterações.'],
          warnings: ['Não some ELT e GL como se fossem um único saldo.', 'Valor de pool não é necessariamente o valor disponível para cada representante.'],
        },
        {
          id: 'vigencia-e-bloqueios-de-budget',
          title: 'Vigência e bloqueios de budget',
          summary: 'Budget futuro ou vencido não libera consumo atual.',
          roles: ['representante', 'supervisor', 'comercial', 'administrador'],
          keywords: ['vigência', 'expirado', 'futuro', 'bloqueio'],
          paragraphs: [
            'A validação considera a data e a categoria. Se a categoria possui budget aplicável, mas os registros estão apenas no futuro ou vencidos, a WD é bloqueada; budget expirado não deve ser ignorado como se não existisse.',
            'Há cenários configurados sem regime de budget aplicável ou sem supervisor associado em que a validação pode ser dispensada. Isso não autoriza contornar um budget ELT ou GL existente e fora da vigência.',
          ],
          steps: ['Confira a data da WD.', 'Abra o budget da categoria correta.', 'Verifique início, fim e situação da vigência.', 'Regularize a vigência ou alocação antes de reenviar.'],
          tips: ['Analise avisos de vencimento antes do último dia.', 'Em WD mista, faça a conferência para ELT e GL.'],
          warnings: ['Não use budget futuro antes da data inicial.', 'Não espere que budget vencido seja pulado automaticamente.'],
        },
        {
          id: 'consumo-e-retorno-de-saldo',
          title: 'Consumo e retorno de saldo',
          summary: 'Quando o saldo é reservado, consumido ou devolvido.',
          roles: ['representante', 'supervisor', 'comercial', 'administrador'],
          keywords: ['consumo', 'devolução de saldo', 'histórico', 'budget'],
          paragraphs: [
            'O saldo é validado antes do envio externo e o consumo é registrado depois que o pedido é persistido. O controle evita que duas operações gastem a mesma disponibilidade.',
            'Cancelamentos, rejeições ou ajustes que prevejam retorno devem aparecer no histórico de budget. O retorno depende da etapa e da ação registrada; não presuma recomposição apenas por deixar de ver o pedido em uma lista.',
          ],
          steps: ['Anote o saldo antes da operação.', 'Envie ou aprove a WD conforme o fluxo.', 'Consulte valor gasto e histórico.', 'Se houver cancelamento com retorno esperado, confirme o lançamento de recomposição.'],
          tips: ['Compare o valor da categoria do pedido com o lançamento.', 'Em divergência, informe pedido, categoria e período.'],
          warnings: ['Não ajuste saldo manualmente para mascarar uma operação.', 'Falha posterior de comunicação pode exigir conferência operacional do histórico.'],
        },
      ],
    },
    {
      id: 'aprovacao-de-doacoes',
      title: 'Aprovação de doações',
      summary: 'Análise do supervisor, edição controlada e aprovador alternativo.',
      icon: 'UserCheckIcon',
      articles: [
        {
          id: 'aprovar-ou-rejeitar-wd',
          title: 'Aprovar ou rejeitar WD',
          summary: 'Faça uma decisão consciente e registre o resultado.',
          roles: ['supervisor', 'administrador'],
          keywords: ['aprovar', 'rejeitar', 'WD', 'supervisor'],
          paragraphs: [
            'Somente um aprovador autorizado pode assumir e decidir uma WD pendente. A proteção evita decisões simultâneas sobre o mesmo pedido.',
            'Antes de aprovar, confira cliente, justificativa, itens, quantidades, categoria e budget. Ao rejeitar, registre uma orientação clara quando o campo estiver disponível.',
          ],
          steps: ['Abra a fila de pedidos aguardando aprovação.', 'Selecione a WD e leia o detalhe completo.', 'Confirme budget e eventuais alterações.', 'Escolha Aprovar ou Rejeitar e confira a mensagem final.'],
          tips: ['Atualize a lista antes de decidir um item aberto há muito tempo.', 'Use o histórico para conferir quem alterou o pedido.'],
          warnings: ['Não aprove apenas pelo total.', 'Se outro aprovador já decidiu, não tente repetir a ação.'],
        },
        {
          id: 'editar-wd-em-aprovacao',
          title: 'Editar WD em aprovação',
          summary: 'Supervisor pode ajustar itens e quantidades dentro da mesma categoria.',
          roles: ['supervisor', 'administrador'],
          keywords: ['editar aprovação', 'quantidade', 'log', 'categoria'],
          paragraphs: [
            'Enquanto estiver Em aprovação, o supervisor autorizado pode editar quantidades e itens. As mudanças ficam registradas no histórico.',
            'A edição não pode introduzir a outra categoria. Uma WD ELT continua ELT, e uma WD GL continua GL.',
          ],
          steps: ['Abra a WD em aprovação.', 'Use Editar quando a ação estiver disponível.', 'Ajuste itens ou quantidades sem trocar a categoria.', 'Salve, revise o histórico e então decida a aprovação.'],
          tips: ['Explique alterações relevantes à pessoa solicitante.', 'Reconfira o saldo depois de reduzir ou aumentar quantidades.'],
          warnings: ['Não use a edição para transformar ELT em GL.', 'Alterações ficam auditáveis.'],
        },
        {
          id: 'usar-aprovador-alternativo',
          title: 'Usar aprovador alternativo',
          summary: 'Delegação permite continuidade sem compartilhar acesso.',
          roles: ['supervisor', 'administrador'],
          keywords: ['delegação', 'aprovador alternativo', 'supervisor'],
          paragraphs: [
            'Uma delegação ativa pode autorizar outro supervisor a aprovar em nome do responsável durante o período definido. A identidade de quem executa a ação continua registrada.',
            'A delegação deve ser criada e encerrada pelas opções autorizadas. Há controle para evitar duplicidade de delegações ativas e falsa identificação.',
          ],
          steps: ['Confirme a ausência ou necessidade operacional.', 'Cadastre ou consulte a delegação nas opções disponíveis.', 'Verifique delegado e período ativo.', 'O delegado acessa a fila com a própria conta e registra a decisão.'],
          tips: ['Defina somente o período necessário.', 'Revogue a delegação quando ela deixar de ser necessária.'],
          warnings: ['Nunca compartilhe a senha do supervisor titular.', 'Delegação não amplia outros acessos além do escopo configurado.'],
        },
      ],
    },
    {
      id: 'acompanhamento-de-pedidos',
      title: 'Acompanhamento de pedidos',
      summary: 'Situações, números, itens parciais e exportação.',
      icon: 'ClipboardIcon',
      articles: [
        {
          id: 'entender-numeros-do-pedido',
          title: 'Entender número do Portal, número ERP e OC',
          summary: 'Use o identificador certo ao pesquisar ou pedir ajuda.',
          roles: [],
          keywords: ['número Portal', 'número ERP', 'OC', 'pedido'],
          paragraphs: [
            'O número do Portal identifica o registro no SalesFlow. O número ERP identifica o processamento no sistema comercial. OC é a ordem de compra informada pelo cliente.',
            'Os três números podem coexistir e não devem ser tratados como iguais. Um deles pode ainda não existir em etapas iniciais.',
          ],
          steps: ['Abra o detalhe do pedido.', 'Localize Número Portal, Número ERP e OC.', 'Anote o rótulo junto de cada valor.', 'Use o campo correspondente nos filtros ou no atendimento.'],
          tips: ['Pesquise primeiro pelo número Portal quando a criação foi recente.', 'Inclua cliente e data se só tiver a OC.'],
          warnings: ['Não informe um número sem dizer de qual tipo ele é.', 'Ausência de número ERP pode significar processamento pendente.'],
        },
        {
          id: 'interpretar-situacoes-do-pedido',
          title: 'Interpretar situações do pedido',
          summary: 'Diferencie preparação, aprovação e aceite comercial.',
          roles: [],
          keywords: ['status', 'situação', 'ERP', 'aceito'],
          paragraphs: [
            'Rascunho indica preparação. Aguardando aprovação indica WD submetida. Aceito pelo ERP indica que a integração comercial recebeu o pedido; outras situações devem ser lidas conforme a descrição exibida.',
            'Consulte o histórico para entender a sequência. Uma mensagem intermediária não deve ser convertida em promessa de faturamento.',
          ],
          steps: ['Pesquise o pedido.', 'Leia a situação atual e a data de atualização.', 'Abra o histórico.', 'Tome a ação indicada ou aguarde a próxima atualização.'],
          tips: ['Atualize a consulta antes de escalar um atraso.', 'Registre a mensagem exata de erro ou pendência.'],
          warnings: ['Pedido no ERP não significa nota emitida.', 'Não crie duplicata para acelerar processamento.'],
        },
        {
          id: 'pedidos-parciais-e-backorder',
          title: 'Pedidos parciais, saldo e backorder',
          summary: 'Acompanhe quantidade atendida e política de pendência.',
          roles: [],
          keywords: ['parcial', 'backorder', 'saldo', 'estoque'],
          paragraphs: [
            'Quando o estoque não atende tudo, um pedido pode ter quantidade atendida e saldo pendente, conforme a política comercial do cliente. O comportamento de backorder não é igual para todos os clientes.',
            'Confira item por item e a situação atual. A parte pendente pode seguir, ser cancelada ou exigir nova orientação conforme a política cadastrada.',
          ],
          steps: ['Abra o detalhe do pedido.', 'Compare quantidade pedida, atendida e pendente.', 'Consulte a situação do saldo.', 'Confirme a política do cliente antes de prometer reposição ou cancelar.'],
          tips: ['Use o ISBN ao comunicar um atendimento parcial.', 'Acompanhe futuras atualizações no mesmo pedido quando houver backorder.'],
          warnings: ['Não recrie automaticamente o saldo pendente.', 'Não prometa data de atendimento sem confirmação de estoque.'],
        },
      ],
    },
    {
      id: 'portal-notas-e-boletos',
      title: 'Portal, notas e boletos',
      summary: 'Filtros, NF, DANFE, XML, boletos e exportações.',
      icon: 'FileTextIcon',
      articles: [
        {
          id: 'consultar-e-exportar-pedidos',
          title: 'Consultar, filtrar e exportar pedidos',
          summary: 'Encontre registros sem confundir o recorte da tela.',
          roles: [],
          keywords: ['filtros', 'exportar', 'CSV', 'pedidos'],
          paragraphs: [
            'A lista de pedidos permite combinar período, cliente, situação e números disponíveis. A exportação representa o conjunto indicado pela tela e pelos filtros.',
            'Antes de exportar, confira se a lista contém o período desejado. O arquivo é uma fotografia da consulta naquele momento.',
          ],
          steps: ['Abra Pedidos.', 'Preencha os filtros e execute a busca.', 'Confira a quantidade e algumas linhas do resultado.', 'Selecione Exportar pedidos exibidos quando precisar do arquivo.'],
          tips: ['Comece com poucos filtros e refine.', 'Abra o arquivo exportado preservando identificadores longos como texto.'],
          warnings: ['Não trate exportação antiga como posição atual.', 'Respeite a confidencialidade dos dados do cliente.'],
        },
        {
          id: 'consultar-nota-danfe-e-xml',
          title: 'Consultar NF, DANFE e XML',
          summary: 'Abra somente documentos realmente retornados para a nota.',
          roles: [],
          keywords: ['NF', 'DANFE', 'XML', 'nota fiscal'],
          paragraphs: [
            'DANFE é a representação em PDF da nota e XML é o arquivo fiscal digital. A disponibilidade depende do retorno real da nota selecionada; resposta vazia não é documento.',
            'Use a identidade da própria NF. A abertura de DANFE na tela de Boletos ainda depende de validação do retorno vigente da Macmillan; se o botão não entregar conteúdo, peça suporte em vez de afirmar que o arquivo existe.',
          ],
          steps: ['Localize o pedido, a nota ou o boleto dentro do escopo do cliente.', 'Confirme número e série da NF.', 'Selecione a ação de DANFE ou XML disponível.', 'Verifique se o arquivo abriu ou foi baixado com conteúdo.'],
          tips: ['Mantenha bloqueios de download do navegador sob controle.', 'Informe número e série da NF ao relatar documento vazio.'],
          warnings: ['Não renomeie um arquivo vazio como comprovante.', 'Nem toda nota terá documentos disponíveis no mesmo momento.'],
        },
        {
          id: 'consultar-boletos-e-copiar-linha',
          title: 'Consultar boletos e copiar dados',
          summary: 'Use filtros e confirme o título antes de copiar.',
          roles: [],
          keywords: ['boleto', 'copiar', 'status', 'nota'],
          paragraphs: [
            'A consulta de boletos respeita o cliente e pode apresentar NF, número Portal e OC como apoio. O vínculo usa os dados disponíveis e diferenças de formatação podem existir.',
            'O status deve ser lido exatamente como retornado na listagem. Não presuma pago, vencido ou disponível sem a indicação atual.',
          ],
          steps: ['Abra Boletos no Portal.', 'Filtre por cliente, período, NF ou número disponível.', 'Confira beneficiário, valor, vencimento e referência.', 'Use Copiar na informação desejada e confira o conteúdo antes de pagar ou enviar.'],
          tips: ['Compare série e número da NF quando houver mais de um título.', 'Atualize a busca para obter a situação mais recente.'],
          warnings: ['Não pague usando somente um número copiado sem conferir os demais dados.', 'Falha no enriquecimento com pedido ou OC não deve ser interpretada como inexistência do boleto.'],
        },
      ],
    },
    {
      id: 'devolucoes-e-trocas',
      title: 'Devoluções e trocas',
      summary: 'Abra solicitações, acompanhe etapas e consulte saldo.',
      icon: 'CornerUpLeftIcon',
      articles: [
        {
          id: 'abrir-solicitacao-de-devolucao',
          title: 'Abrir solicitação de devolução',
          summary: 'Registre itens e motivo quando o recurso estiver ativo.',
          roles: [],
          keywords: ['devolução', 'solicitação', 'motivo', 'troca'],
          paragraphs: [
            'O menu Solicitações pode estar ativo ou oculto por preferência da Macmillan. Quando ativo, use Nova Devolução para registrar os produtos e o motivo.',
            'A política de prazo, troca parcial, transporte e documentos obrigatórios deve ser confirmada na orientação operacional vigente; não deduza essas regras apenas pela tela.',
          ],
          steps: ['Abra Devoluções e Trocas e selecione Nova Devolução.', 'Escolha cliente e referência solicitada.', 'Informe itens, quantidades e motivo com precisão.', 'Revise e envie a solicitação.'],
          tips: ['Tenha a nota de origem e os ISBNs antes de começar.', 'Descreva avaria ou divergência de forma objetiva.'],
          warnings: ['Enviar solicitação não autoriza despachar mercadoria.', 'Se o menu estiver oculto, peça o canal operacional correto.'],
        },
        {
          id: 'acompanhar-etapas-da-devolucao',
          title: 'Acompanhar as etapas da devolução',
          summary: 'Entenda a sequência do pedido até a conclusão.',
          roles: [],
          keywords: ['AGUARDANDO', 'EM ANÁLISE', 'COLETA', 'CONCLUÍDO'],
          paragraphs: [
            'A sequência registrada é Aguardando, Em análise, Aprovado, Coleta agendada, Coletado, Recebido e Concluído. Cada etapa informa avanço operacional, não aprovação automática das próximas.',
            'Quem pode mover cada etapa e os prazos de atendimento dependem da operação adotada pela Macmillan. Consulte o detalhe e as orientações recebidas.',
          ],
          steps: ['Abra a lista de Solicitações.', 'Pesquise pelo cliente ou identificador.', 'Abra Detalhes da Devolução.', 'Leia situação, histórico e instrução antes de qualquer envio ou coleta.'],
          tips: ['Acompanhe até Concluído.', 'Registre comprovantes pelo canal indicado na operação.'],
          warnings: ['Aprovado não é sinônimo de Recebido.', 'Não marque ou presuma coleta sem confirmação.'],
        },
        {
          id: 'consultar-saldo-de-devolucao',
          title: 'Consultar Saldo de Devolução',
          summary: 'Leia contrato, períodos, valores pendentes e saldo final.',
          roles: [],
          keywords: ['saldo de devolução', 'contrato', 'emitente', 'limite'],
          paragraphs: [
            'Saldo de Devolução permanece disponível mesmo quando Solicitações está desativado. A lista permite consultar emitente, contrato, tipo, períodos, limite, valores confirmados, pendentes e saldo final.',
            'Use o ícone de visualização para abrir o detalhe. Saldo pendente e saldo confirmado têm significados diferentes e não devem ser somados sem observar os rótulos.',
          ],
          steps: ['Abra Devoluções e Trocas, Saldo de Devolução.', 'Filtre por emitente, contrato, situação ou período.', 'Localize a linha correta.', 'Use Visualizar e confira a composição do saldo.'],
          tips: ['Comece com Ativo igual a Sim.', 'Confirme o período de faturamento e de devolução.'],
          warnings: ['Saldo exibido não substitui autorização da solicitação.', 'Filtros de tipo e período podem atuar sobre os dados carregados na página; navegue pelas páginas ao conferir o conjunto.'],
        },
      ],
    },
    {
      id: 'guia-de-devolucao',
      title: 'Guia de Devolução',
      summary: 'Consulte a orientação operacional disponível na tela.',
      icon: 'MapIcon',
      articles: [
        {
          id: 'abrir-guia-de-devolucao',
          title: 'Abrir o Guia de Devolução',
          summary: 'O guia é uma janela de orientação, não um arquivo gerado por solicitação.',
          roles: [],
          keywords: ['Guia de Devolução', 'saldo', 'tutorial'],
          paragraphs: [
            'Na página Saldo de Devolução da Macmillan, o botão Guia de Devolução abre uma orientação operacional com passo a passo e regras. A implementação não gera uma guia individual nem comprova download de arquivo.',
            'Leia o conteúdo exibido na sessão atual, pois períodos, condições e contatos operacionais podem mudar. Este manual não reproduz dados sensíveis ou informações temporárias do guia.',
          ],
          steps: ['Abra Devoluções e Trocas, Saldo de Devolução.', 'Selecione Guia de Devolução.', 'Leia Passo a passo, Período e condições e demais orientações.', 'Feche a janela somente depois de registrar a ação necessária.'],
          tips: ['Consulte o guia antes de preparar documentos ou transporte.', 'Confirme datas diretamente na janela atual.'],
          warnings: ['Não procure um arquivo gerado automaticamente: essa função não foi identificada.', 'Se precisar baixar um documento e não houver opção, peça suporte ou orientação operacional.'],
        },
        {
          id: 'preparar-documentos-da-devolucao',
          title: 'Preparar documentos da devolução',
          summary: 'Confira a orientação vigente antes do despacho.',
          roles: [],
          keywords: ['nota fiscal', 'XML', 'documentos', 'despacho'],
          paragraphs: [
            'O guia apresenta os dados que a nota deve conter e orientações sobre envio do XML e entrega. Use os dados exibidos no próprio guia, sem reutilizar captura antiga.',
            'A solicitação no portal e os documentos fiscais são partes diferentes do processo. Uma etapa não substitui a outra.',
          ],
          steps: ['Abra o guia vigente.', 'Compare cada item exigido com a nota de origem e os produtos.', 'Siga o canal de validação mostrado.', 'Aguarde a confirmação operacional antes de despachar.'],
          tips: ['Revise ISBN e quantidades produto por produto.', 'Guarde o retorno de validação pelo período necessário.'],
          warnings: ['Não envie mercadoria com dados copiados de um guia antigo.', 'Não prometa aceite antes da conferência física.'],
        },
        {
          id: 'resolver-duvidas-sobre-o-guia',
          title: 'Resolver dúvidas sobre o guia',
          summary: 'O que fazer quando botão, instrução ou download não estiver disponível.',
          roles: [],
          keywords: ['guia ausente', 'download', 'suporte', 'dúvida'],
          paragraphs: [
            'Se o botão não aparecer, confirme que está no tenant Macmillan e na página Saldo de Devolução. A função é específica desse contexto.',
            'Se a orientação não responder à dúvida sobre prazo, transportadora, coleta ou troca parcial, peça confirmação ao canal operacional. Essas regras não devem ser inventadas.',
          ],
          steps: ['Confirme página, perfil e empresa acessada.', 'Atualize a página e procure Guia de Devolução.', 'Registre a dúvida e a etapa atual da solicitação.', 'Acione suporte ou o contato operacional indicado na própria tela.'],
          tips: ['Informe identificador da solicitação sem enviar senha.', 'Descreva se a necessidade é consultar orientação ou baixar um documento.'],
          warnings: ['Não compartilhe dados fiscais em canal público.', 'Não trate ausência de botão de download como falha na solicitação.'],
        },
      ],
    },
    {
      id: 'cadastros-e-preferencias',
      title: 'Cadastros e preferências',
      summary: 'Mantenha usuários, endereços e opções operacionais coerentes.',
      icon: 'SettingsIcon',
      articles: [
        {
          id: 'administrar-usuarios-e-acessos',
          title: 'Administrar usuários e acessos',
          summary: 'Conceda somente o necessário para a função.',
          roles: ['administrador'],
          keywords: ['usuário', 'acesso', 'perfil', 'inativar'],
          paragraphs: [
            'Usuários devem ter identificação própria, perfis adequados e vínculos atualizados. A permissão do tipo de pedido é separada do simples acesso ao menu.',
            'Ao mudar função ou desligar uma pessoa, revise conta, carteira, delegações e perfis.',
          ],
          steps: ['Abra o cadastro de usuários disponível.', 'Localize a pessoa correta.', 'Revise situação, perfis e vínculos.', 'Salve e peça que a pessoa confirme com a própria conta.'],
          tips: ['Adote revisão periódica de acessos.', 'Registre o motivo de alterações conforme o processo interno.'],
          warnings: ['Não crie contas coletivas.', 'Não conceda Administrador apenas para resolver uma tarefa pontual.'],
        },
        {
          id: 'manter-enderecos-do-cliente',
          title: 'Manter endereços do cliente',
          summary: 'Corrija o cadastro sem alterar o histórico de pedidos.',
          roles: ['comercial', 'administrador'],
          keywords: ['cadastro', 'endereço', 'cliente', 'histórico'],
          paragraphs: [
            'Endereços devem ser mantidos com CEP, cidade e UF coerentes. Campos disponíveis podem variar conforme o cadastro da empresa.',
            'Pedidos com endereço alternativo guardam um retrato dos dados usados. Uma correção posterior melhora novos pedidos, mas não reescreve o endereço dos antigos.',
          ],
          steps: ['Localize o cliente.', 'Abra os endereços disponíveis.', 'Confira os dados com uma fonte autorizada.', 'Salve e valide a escolha em um novo pedido.'],
          tips: ['Evite abreviações ambíguas.', 'Confirme se o endereço está ativo antes de usar.'],
          warnings: ['Não exclua endereço com histórico sem avaliar o impacto.', 'Não use regra de planilha antiga para decidir quem pode selecionar endereço.'],
        },
        {
          id: 'entender-preferencias-da-empresa',
          title: 'Entender preferências da empresa',
          summary: 'Recursos podem ser ligados ou desligados para a Macmillan.',
          roles: ['administrador'],
          keywords: ['preferências', 'rascunho', 'devolução', 'configuração'],
          paragraphs: [
            'Preferências controlam recursos como rascunho, rascunho somente para WD, perfis de rascunho, Representante apenas doação e Solicitações de Devolução.',
            'Uma alteração pode afetar muitos usuários. Antes de salvar, entenda o alcance e combine a mudança com a operação.',
          ],
          steps: ['Abra Preferências.', 'Localize a opção pelo nome.', 'Leia o estado atual e o efeito esperado.', 'Altere somente com autorização e comunique os usuários afetados.'],
          tips: ['Registre a data e o responsável pela mudança.', 'Teste a regra com o perfil adequado pelo processo da equipe.'],
          warnings: ['Desligar Solicitações oculta esse menu, mas não o Saldo de Devolução.', 'Não altere uma preferência para contornar um pedido individual.'],
        },
      ],
    },
    {
      id: 'problemas-comuns',
      title: 'Problemas comuns',
      summary: 'Diagnóstico simples para acesso, produtos e pedidos.',
      icon: 'AlertCircleIcon',
      articles: [
        {
          id: 'nao-consigo-acessar-ou-ver-menu',
          title: 'Não consigo acessar ou ver um menu',
          summary: 'Confira sessão, perfil, vínculo e preferência.',
          roles: [],
          keywords: ['menu ausente', 'acesso negado', 'login', 'perfil'],
          paragraphs: [
            'Menu ausente geralmente está relacionado ao perfil ativo, vínculo, permissão ou preferência da empresa. Primeiro descarte sessão expirada e contexto incorreto.',
            'Um colega enxergar a opção não prova que sua conta deve enxergar, pois o escopo pode ser diferente.',
          ],
          steps: ['Saia e entre novamente pela página oficial.', 'Confirme o perfil ativo.', 'Verifique cliente ou carteira.', 'Peça conferência informando usuário, perfil e nome do menu.'],
          tips: ['Inclua horário e mensagem exata.', 'Teste sem usar favoritos de páginas internas antigas.'],
          warnings: ['Não peça senha de colega para comparar.', 'Não altere preferências globais sem autorização.'],
        },
        {
          id: 'produto-preco-ou-estoque-divergente',
          title: 'Produto, preço ou estoque divergente',
          summary: 'Reúna o contexto atual antes de solicitar análise.',
          roles: [],
          keywords: ['produto não encontrado', 'preço divergente', 'estoque'],
          paragraphs: [
            'Produto sem resultado pode ser código incorreto, filtro, categoria ou contexto. Preço depende de cliente e tabela; estoque pode mudar entre consulta e fechamento.',
            'Imposto e total podem ser recalculados. A melhor evidência é o contexto atual, não uma imagem histórica.',
          ],
          steps: ['Confirme ISBN, cliente, tabela e categoria.', 'Limpe filtros e pesquise novamente.', 'Atualize o carrinho e revise o fechamento.', 'Se persistir, informe os dados e a mensagem exibida.'],
          tips: ['Compare códigos completos.', 'Informe quantidade, pois ela pode afetar disponibilidade.'],
          warnings: ['Não force item semelhante.', 'Não use preço histórico como correção manual.'],
        },
        {
          id: 'pedido-nao-avanca',
          title: 'Pedido não avança',
          summary: 'Interprete a validação em vez de criar duplicata.',
          roles: [],
          keywords: ['erro no pedido', 'não avança', 'validação', 'duplicidade'],
          paragraphs: [
            'Bloqueios comuns incluem mistura WS e WC, tipo não permitido, endereço ou UF inválidos, item sem preço, budget insuficiente ou fora da vigência e ID Nexus WD com formato incorreto.',
            'A mensagem costuma indicar o ponto a corrigir. Se o processamento já começou, consulte a lista antes de tentar novamente.',
          ],
          steps: ['Leia e copie a mensagem completa.', 'Revise tipo, itens, endereço, Nexus e budget conforme o caso.', 'Corrija somente o dado indicado.', 'Consulte Pedidos antes de reenviar.'],
          tips: ['Anote o número Portal se ele tiver sido exibido.', 'Em WD mista, verifique ELT e GL separadamente.'],
          warnings: ['Não clique várias vezes em Confirmar.', 'Não troque o tipo apenas para eliminar a mensagem.'],
        },
      ],
    },
    {
      id: 'perguntas-frequentes',
      title: 'Perguntas frequentes',
      summary: 'Respostas rápidas para dúvidas recorrentes.',
      icon: 'HelpCircleIcon',
      articles: [
        {
          id: 'faq-pedidos-e-categorias',
          title: 'Perguntas sobre pedidos e categorias',
          summary: 'Respostas diretas sobre WS, WC, WD e criação.',
          roles: [],
          keywords: ['FAQ', 'WS', 'WC', 'WD', 'pedido'],
          paragraphs: [
            'Posso misturar WS e WC? Não. Faça pedidos separados. Cliente pode criar WD? Não. WD tem valor mínimo? Não, mas continua sujeito a permissão, budget e aprovação.',
            'Representante ou Supervisor só pode WD? Não como regra geral. Vale o perfil ativo e a relação de tipos permitidos na configuração da Macmillan.',
          ],
          steps: ['Identifique a finalidade.', 'Confira o tipo oferecido ao perfil ativo.', 'Separe categorias incompatíveis.', 'Siga a validação apresentada.'],
          tips: ['Quando um tipo não aparece, peça conferência em vez de escolher outro.', 'Use os nomes WS, WC e WD ao pedir ajuda.'],
          warnings: ['Não aplique permissões de outro tenant.', 'Não trate preferência temporária como regra eterna.'],
        },
        {
          id: 'faq-documentos-e-status',
          title: 'Perguntas sobre documentos e situações',
          summary: 'Respostas sobre ERP, DANFE, XML e boleto.',
          roles: [],
          keywords: ['FAQ', 'ERP', 'DANFE', 'XML', 'boleto'],
          paragraphs: [
            'Pedido aceito pelo ERP já foi faturado? Não necessariamente. DANFE e XML sempre aparecem? Não; dependem do retorno real da nota. Boleto sem número Portal está inválido? Não necessariamente; o enriquecimento pode estar indisponível.',
            'Número Portal, número ERP e OC são iguais? Não. Sempre informe o rótulo do identificador.',
          ],
          steps: ['Abra o detalhe atual.', 'Confirme a situação e os números.', 'Tente o documento na nota correta.', 'Se vazio, informe número e série ao suporte.'],
          tips: ['Atualize a consulta antes de concluir.', 'Confira o cliente para evitar nota homônima.'],
          warnings: ['Não invente significado para status de boleto.', 'Não prometa arquivo que ainda não foi retornado.'],
        },
        {
          id: 'quando-pedir-ajuda',
          title: 'Quando e como pedir ajuda',
          summary: 'Envie contexto suficiente sem expor dados sensíveis.',
          roles: [],
          keywords: ['suporte', 'ajuda', 'erro', 'atendimento'],
          paragraphs: [
            'Peça ajuda quando a validação continuar após revisar os dados, quando uma permissão esperada estiver ausente ou quando documento e histórico não refletirem a operação.',
            'Informe perfil ativo, cliente por código, número Portal ou ERP, horário, etapa, filtros e mensagem exata. Oculte dados pessoais e financeiros desnecessários.',
          ],
          steps: ['Reproduza o problema uma vez com atenção.', 'Anote contexto e mensagem.', 'Verifique se o registro foi criado.', 'Envie a solicitação pelo canal autorizado.'],
          tips: ['Descreva o resultado esperado e o observado.', 'Use captura somente da área necessária e sem informações confidenciais.'],
          warnings: ['Nunca envie senha, token ou dados bancários completos.', 'Não compartilhe relatórios públicos com informações de clientes.'],
        },
      ],
    },
    {
      id: 'videos-e-aprendizado',
      title: 'Vídeos e aprendizado',
      summary: 'Use materiais visuais disponíveis sem substituir as regras atuais.',
      icon: 'VideoIcon',
      articles: [
        {
          id: 'encontrar-videos-de-ajuda',
          title: 'Encontrar vídeos de ajuda',
          summary: 'Localize conteúdos vinculados à Central de Ajuda.',
          roles: [],
          keywords: ['vídeo', 'tutorial', 'ajuda', 'treinamento'],
          paragraphs: [
            'A Central de Ajuda pode apresentar vídeos por assunto quando houver material publicado para a Macmillan. A ausência de vídeo não impede o uso dos artigos em texto.',
            'Vídeos refletem a tela da data de gravação. Confirme nomes de botões e regras na versão atual do portal.',
          ],
          steps: ['Abra a Central de Ajuda.', 'Selecione a seção do assunto.', 'Procure o artigo ou vídeo disponível.', 'Após assistir, retorne ao artigo para conferir alertas e pré-requisitos.'],
          tips: ['Pause o vídeo e execute uma etapa por vez.', 'Use pesquisa por palavra-chave.'],
          warnings: ['Não há garantia de vídeo para toda operação.', 'Não abra links de treinamento fora dos canais oficiais.'],
        },
        {
          id: 'aprender-fluxo-de-pedido',
          title: 'Roteiro de aprendizado para pedidos',
          summary: 'Sequência recomendada para novos usuários.',
          roles: [],
          keywords: ['treinamento', 'pedido', 'catálogo', 'checkout'],
          paragraphs: [
            'Comece por perfil ativo e categorias, depois catálogo, carrinho, criação e finalização. Usuários de WD devem estudar também budget e aprovação.',
            'Pratique com uma operação autorizada e dados reais de trabalho; não crie pedidos fictícios em ambiente produtivo.',
          ],
          steps: ['Leia Primeiros passos e Perfis.', 'Estude WS, WC e WD.', 'Revise Catálogo, Carrinho e Criação.', 'Complete com Finalização, Acompanhamento e, se aplicável, Budget.'],
          tips: ['Use este roteiro no treinamento de novos integrantes.', 'Tire dúvidas antes de confirmar uma operação real.'],
          warnings: ['Não use valores ou clientes inventados em produção.', 'Não pule a conferência final.'],
        },
        {
          id: 'manter-se-atualizado',
          title: 'Manter-se atualizado',
          summary: 'Combine manual, mural e orientação operacional.',
          roles: [],
          keywords: ['atualização', 'mural', 'manual', 'novidade'],
          paragraphs: [
            'Consulte a data de atualização deste manual, o mural e as mensagens da tela. Preferências e permissões podem mudar com a operação da Macmillan.',
            'Quando um vídeo ou comunicado antigo divergir da tela, registre a diferença e confirme a orientação vigente.',
          ],
          steps: ['Revise o mural regularmente.', 'Observe a data dos artigos e vídeos.', 'Compare a orientação com a tela atual.', 'Comunique divergências pelo canal de suporte.'],
          tips: ['Compartilhe o link do artigo, não uma cópia desatualizada.', 'Revisite os temas usados com pouca frequência.'],
          warnings: ['Não guarde arquivos com dados de clientes como material de treinamento.', 'Não publique capturas de relatórios ou documentos fiscais.'],
        },
      ],
    },
  ],
  glossary: [
    { term: 'Alocação', definition: 'Parcela de budget destinada a um representante dentro de uma categoria e vigência.' },
    { term: 'Autocomplete', definition: 'Lista de sugestões que ajuda a localizar e selecionar um produto enquanto o código ou nome é digitado.' },
    { term: 'Backorder', definition: 'Saldo de item não atendido de imediato que pode permanecer pendente conforme a política do cliente.' },
    { term: 'Budget', definition: 'Limite controlado para doações, organizado por responsável, categoria, alocação e período de vigência.' },
    { term: 'Carrinho', definition: 'Área temporária onde produtos e quantidades são reunidos antes da finalização.' },
    { term: 'Categoria do pedido', definition: 'Classificação que identifica a finalidade WS, WC ou WD e impede combinações incompatíveis.' },
    { term: 'DANFE', definition: 'Representação auxiliar em PDF de uma nota fiscal eletrônica, quando o documento é retornado para consulta.' },
    { term: 'Delegação', definition: 'Autorização temporária para um aprovador alternativo atuar com sua própria conta.' },
    { term: 'ELT', definition: 'Categoria de budget usada para itens classificados como idioma inglês.' },
    { term: 'ERP', definition: 'Sistema comercial que recebe e processa pedidos, notas e outros retornos operacionais.' },
    { term: 'GL', definition: 'Categoria de budget usada para itens que não são classificados como inglês; substitui a denominação antiga OL.' },
    { term: 'ID Nexus', definition: 'Identificador opcional exclusivo de WD que, quando informado, deve ter exatamente seis dígitos.' },
    { term: 'ISBN', definition: 'Código internacional que identifica uma edição de livro e ajuda a evitar seleção de produto semelhante.' },
    { term: 'Mural', definition: 'Área de comunicados, campanhas e orientações visíveis aos públicos definidos.' },
    { term: 'Número ERP', definition: 'Identificador atribuído ao pedido no sistema comercial após o processamento correspondente.' },
    { term: 'Número Portal', definition: 'Identificador do registro do pedido dentro do SalesFlow.' },
    { term: 'OC', definition: 'Ordem de compra informada pelo cliente, diferente do número Portal e do número ERP.' },
    { term: 'Perfil ativo', definition: 'Papel selecionado na sessão que determina escopo, menus e tipos de operação disponíveis naquele momento.' },
    { term: 'Pool', definition: 'Montante de budget de uma categoria sob responsabilidade do supervisor antes ou durante as alocações.' },
    { term: 'Rascunho', definition: 'Pedido salvo em preparação, ainda não submetido à aprovação nem aceito pelo ERP.' },
    { term: 'Saldo de Devolução', definition: 'Consulta de limites e valores confirmados ou pendentes relacionados ao processo de devolução.' },
    { term: 'Snapshot de endereço', definition: 'Cópia dos dados de entrega guardada no pedido, preservada mesmo se o cadastro mudar depois.' },
    { term: 'Tenant', definition: 'Ambiente próprio da empresa, com preferências, permissões e dados separados de outras empresas.' },
    { term: 'Vigência', definition: 'Intervalo de datas em que um budget, alocação ou regra pode ser utilizado.' },
    { term: 'WC', definition: 'Tipo de pedido destinado à Certificação; não pode compartilhar o mesmo pedido com itens WS.' },
    { term: 'WD', definition: 'Tipo de pedido destinado à Doação, sem valor mínimo, sujeito a permissão, budget quando aplicável e aprovação.' },
    { term: 'WS', definition: 'Tipo de pedido destinado à Venda; não pode compartilhar o mesmo pedido com itens WC.' },
    { term: 'XML', definition: 'Arquivo fiscal digital da nota eletrônica, disponível somente quando houver retorno válido para a nota.' },
  ],
};